import { useCallback, useRef } from "react";
import { useAiActionsModal } from "@/react/components/SettingsApp/Plan/AiActionsModal";
import type { Action } from "@/react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { useShouldShowAiModal } from "./useShouldShowAiModal";

interface UseAiWorkflowActivationModalOptions {
  actions?: Action[];
  steps?: Action["action_type"][];
  workflowId?: string;
}

// Utility function to check if workflow has AI-related actions
const hasAiActions = (
  actions?: Action[],
  steps?: Action["action_type"][],
): boolean => {
  if (actions?.length) {
    return actions.some(action => action.action_type?.startsWith("ai_"));
  }
  if (steps?.length) {
    return steps.some(step => step?.startsWith("ai_"));
  }
  return false;
};

export const useAiWorkflowActivationModal = ({
  actions,
  steps,
  workflowId,
}: UseAiWorkflowActivationModalOptions = {}) => {
  const aiActionsModal = useAiActionsModal();
  const shouldShowAiModal = useShouldShowAiModal();
  const hasWorkflowAiActions = hasAiActions(actions, steps);

  // Track which workflow triggered the modal
  const triggeringWorkflowRef = useRef<string | null>(null);

  const shouldBlockActivation = shouldShowAiModal && hasWorkflowAiActions;

  // Only consider this workflow affected by modal visibility if it's the one that triggered it
  const isModalVisibleForThisWorkflow =
    aiActionsModal.visible &&
    (workflowId ? triggeringWorkflowRef.current === workflowId : false);

  const handleActivationAttempt = useCallback(
    (proceedWithActivation: () => void) => {
      if (shouldBlockActivation) {
        // Mark this workflow as the one triggering the modal
        if (workflowId) {
          triggeringWorkflowRef.current = workflowId;
        }
        void aiActionsModal.show().then(() => {
          // Clear the triggering workflow when modal closes
          triggeringWorkflowRef.current = null;
        });
        return;
      }

      proceedWithActivation();
    },
    [shouldBlockActivation, aiActionsModal, workflowId],
  );

  return {
    shouldBlockActivation,
    handleActivationAttempt,
    showModal: useCallback(() => aiActionsModal.show(), [aiActionsModal]),
    isModalVisible: isModalVisibleForThisWorkflow,
  };
};
